import { type MouseEvent, lazy, useCallback, useState } from 'react';

import { breakpoints, useTheme } from '@pulse-web-ui/theme';

import { ColumnContainer, useIsDesktop } from '@shared/index';

import { SummaryItem } from './summary-item';
import {
  Container,
  IconButton,
  Separator,
  Title,
  TitleContainer,
} from './summary.styles';
import type { Data } from './summary.types';

const ArrowUpIcon = lazy(() => import('@pulse-web-ui/icons/lib/lazy/arrow-up'));
const CloseIcon = lazy(() => import('@pulse-web-ui/icons/lib/lazy/close'));

interface SummaryProps {
  title: string;
  data?: Data[];
  total?: Data;
}

export const Summary = ({ title, data, total }: SummaryProps) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop(breakpoints.DESKTOP_LARGE);
  const [isOpenState, setIsOpenState] = useState(false);

  const toggleIsOpen = useCallback(
    (isOpen: boolean) => (event: MouseEvent) => {
      if (isDesktop) return;
      setIsOpenState(isOpen);
      event.stopPropagation();
    },
    [isDesktop]
  );

  const isOpen = isOpenState || isDesktop;

  return (
    <Container
      $isOpen={isOpen}
      $withSummary={!!total}
    >
      <TitleContainer
        $isOpen={isOpen}
        $withSummary={!!total}
        onClick={toggleIsOpen(true)}
      >
        {!isDesktop && !isOpen && (
          <ArrowUpIcon color={theme.colors.icon.primary} />
        )}
        <Title $isOpen={isOpen}>{title}</Title>
        {!isDesktop && isOpen && (
          <IconButton onClick={toggleIsOpen(false)}>
            <CloseIcon color={theme.colors.icon.primary} />
          </IconButton>
        )}
      </TitleContainer>
      {isOpen && (
        <ColumnContainer $gap={16}>
          {data?.map((item) => (
            <SummaryItem
              key={item.title.text}
              data={item}
            />
          ))}
        </ColumnContainer>
      )}
      {total && (
        <>
          {isOpen && <Separator />}
          <SummaryItem
            isTotal
            data={total}
          />
        </>
      )}
    </Container>
  );
};
